import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Request from "../Utils/Request/Request";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const options = [
  { text: "QR", value: "qr" },
  { text: "Logo", value: "logo" },
  { text: "Image", value: "template-image" },
];

const getImageUrl = (filename) =>
  filename ? `${API_URL}/image-storage/${encodeURIComponent(filename)}` : "";

const TemplateImage = ({ value, onChange }) => {
  const [templateImages, setTemplateImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadError, setLoadError] = useState("");
  const imageValue = value?.value;
  const selectedType =
    typeof imageValue === "object" && imageValue?.source === "template-image"
      ? "template-image"
      : imageValue || "";

  useEffect(() => {
    if (selectedType !== "template-image") return;

    let active = true;
    setLoadingImages(true);
    setLoadError("");
    Request.get("/template-images")
      .then((response) => {
        if (active) setTemplateImages(response.data || []);
      })
      .catch((error) => {
        if (active) {
          setLoadError(error?.response?.data?.message || "Could not load template images.");
        }
      })
      .finally(() => {
        if (active) setLoadingImages(false);
      });

    return () => {
      active = false;
    };
  }, [selectedType]);

  const selectedImage = useMemo(() => {
    if (typeof imageValue !== "object") return null;
    return templateImages.find((image) => image.id === imageValue.imageId) || imageValue;
  }, [imageValue, templateImages]);

  const handleTypeChange = (event) => {
    const selectedValue = event.target.value;
    onChange({
      type: "image",
      value:
        selectedValue === "template-image"
          ? { source: "template-image", imageId: null, filename: "", originalName: "" }
          : selectedValue,
    });
  };

  const handleImageChange = (event) => {
    const image = templateImages.find((item) => String(item.id) === String(event.target.value));
    if (!image) return;

    onChange({
      type: "image",
      value: {
        source: "template-image",
        imageId: image.id,
        filename: image.filename,
        originalName: image.originalName,
      },
    });
  };

  return (
    <Box>
      <Typography variant="body2" mb={1}>
        Select <span style={{ color: "#365b6d" }}>Image</span> Option
      </Typography>
      <Select fullWidth value={selectedType} onChange={handleTypeChange} displayEmpty>
        <MenuItem value="" disabled>Select an option</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.text}
          </MenuItem>
        ))}
      </Select>

      {selectedType === "template-image" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" mb={1}>Select Template Image</Typography>
          {loadingImages ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading images…</Typography>
            </Box>
          ) : loadError ? (
            <Alert severity="error">{loadError}</Alert>
          ) : (
            <Select
              fullWidth
              displayEmpty
              value={selectedImage?.imageId || ""}
              onChange={handleImageChange}
              renderValue={(selected) => {
                if (!selected) return "Select an image";
                const image = templateImages.find((item) => String(item.id) === String(selected)) || selectedImage;
                return (
                  <Box
                    component="span"
                    title={image?.originalName}
                    sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {image?.originalName || "Selected image"}
                  </Box>
                );
              }}
            >
              <MenuItem value="" disabled>Select an image</MenuItem>
              {templateImages.map((image) => (
                <MenuItem key={image.id} value={image.id} sx={{ gap: 1.5 }}>
                  <Box
                    component="img"
                    src={getImageUrl(image.filename)}
                    alt=""
                    sx={{ width: 48, height: 36, objectFit: "contain", border: "1px solid #dde3e6", borderRadius: 0.5 }}
                  />
                  <Typography
                    variant="body2"
                    noWrap
                    title={image.originalName}
                    sx={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {image.originalName}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          )}

          {selectedImage?.filename && (
            <Box sx={{ mt: 2, p: 1.5, border: "1px solid #d9e0e4", borderRadius: 1, backgroundColor: "#f8fafb" }}>
              <Box
                component="img"
                src={getImageUrl(selectedImage.filename)}
                alt={selectedImage.originalName || "Selected template"}
                sx={{ display: "block", width: "100%", maxHeight: 180, objectFit: "contain" }}
              />
              <Typography
                variant="caption"
                title={selectedImage.originalName}
                noWrap
                sx={{ display: "block", width: "100%", mt: 1, overflow: "hidden", textOverflow: "ellipsis", textAlign: "center" }}
              >
                {selectedImage.originalName}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TemplateImage;
